import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Trophy, Lock, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { DoodleSquiggle } from './Doodles';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AchievementBadges() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  const fetchAchievements = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/achievements`, { withCredentials: true });
      setData(data);
      // Show toast for newly earned
      data.achievements.filter(a => a.new).forEach(a => {
        toast.success(`Achievement Unlocked: ${a.name}!`, { icon: a.icon });
      });
    } catch (e) {
      console.error('Failed to fetch achievements:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAchievements(); }, [fetchAchievements]);

  if (loading) return <div className="neo-card p-8 text-center animate-pulse-soft">Loading achievements...</div>;
  if (!data) return null;

  const earned = data.achievements.filter(a => a.earned);
  const unearned = data.achievements.filter(a => !a.earned);
  const displayList = showAll ? data.achievements : [...earned, ...unearned.slice(0, 4)];

  return (
    <div className="space-y-4" data-testid="achievements">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold font-['Fredoka'] flex items-center gap-2">
          <Trophy className="w-5 h-5" /> Achievements
        </h3>
        <span className="neo-btn px-3 py-1 bg-[#FDF0D5] text-sm font-bold">
          {data.total_earned}/{data.total}
        </span>
      </div>
      <DoodleSquiggle width={100} color="#FFC8DD" />

      {/* Progress bar */}
      <div className="progress-bar" style={{ height: '1rem' }}>
        <div className="progress-fill bg-gradient-to-r from-[#FFC8DD] via-[#A2D2FF] to-[#B9FBC0]" style={{ width: `${(data.total_earned / data.total) * 100}%` }} />
      </div>

      {/* Badges grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {displayList.map((ach) => (
          <div
            key={ach.id}
            className={`neo-card p-4 text-center transition-all ${
              ach.earned ? '' : 'opacity-50'
            } ${ach.new ? 'ring-4 ring-yellow-400 animate-bounce-in' : ''}`}
            data-testid={`achievement-${ach.id}`}
          >
            <div className={`w-14 h-14 mx-auto mb-2 rounded-2xl border-2 flex items-center justify-center text-2xl ${
              ach.earned ? 'border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]' : 'border-gray-300'
            }`}
              style={{ backgroundColor: ach.earned ? '#FDF0D5' : '#f0f0f0' }}
            >
              {ach.earned ? ach.icon : ach.hidden ? '❓' : <Lock className="w-6 h-6 text-gray-400" />}
            </div>
            <h4 className="font-bold font-['Fredoka'] text-sm">{ach.name}</h4>
            <p className="text-xs text-gray-500 dark:text-gray-400">{ach.desc}</p>
            {ach.new && (
              <span className="inline-flex items-center gap-1 mt-1 text-xs font-bold text-yellow-600">
                <Sparkles className="w-3 h-3" /> New!
              </span>
            )}
          </div>
        ))}
      </div>

      {!showAll && unearned.length > 4 && (
        <button
          onClick={() => setShowAll(true)}
          className="neo-btn w-full py-3 bg-white dark:bg-[#252545]"
          data-testid="show-all-achievements"
        >
          Show All ({data.total - displayList.length} more)
        </button>
      )}
    </div>
  );
}
